import { useEffect, useMemo, useState } from "react";
import type { FeatureKey } from "../enums/feature-keys";
import { getRemoteConfigService } from "../services/remote-config-registry";

function isEmptyJsonObject(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  );
}

export function useRemoteConfig() {
  const service = getRemoteConfigService();
  return {
    getString: (key: FeatureKey) => service.getString(key),
    getBoolean: (key: FeatureKey) => service.getBoolean(key),
    getNumber: (key: FeatureKey) => service.getNumber(key),
    getJSON: (key: FeatureKey) => service.getJSON(key),
  };
}

export function useRemoteConfigJSON<T = any>(
  key: string,
  defaultValue: T,
): { value: T; isInitialized: boolean } {
  const service = getRemoteConfigService();
  const [updateVersion, setUpdateVersion] = useState(0);

  useEffect(() => {
    return service.onUpdate(() => {
      setUpdateVersion((version) => version + 1);
    });
  }, [service]);

  const value = useMemo(() => {
    const raw = service.getJSON(key as FeatureKey) as T;
    if (raw == null || isEmptyJsonObject(raw)) {
      return defaultValue;
    }
    return raw;
  }, [defaultValue, key, service, updateVersion]);

  return {
    value,
    isInitialized: true,
  };
}
